import React, { useState } from 'react';
import Form from 'js/components/common/Form';
import { uniqueId } from 'js/utils/methods.util';
import { partition, path, pick, prop } from 'ramda';
import { Button } from 'js/components/common/Button';
import { Input } from 'js/components/common/Fields/Input';
import { getPromoCodeFromStore } from 'js/utils/promocode.util';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { usePortletSelector, usePortletTranslations } from 'js/components/system/Portlet';

import * as hooks from './hooks';
import * as helpers from './helpers';
import { Suggestions } from '../Suggestions';
import { useMethodSelector } from '../../../withCashierPortlet';

import style from './CashierForm.scss';

export const CashierForm = ({ deposit }) => {
    const {
        accountFields = [],
        accounts,
        validationConfig,
        defaultSuggestion,
        suggestions,
        amountRequired
    } = useMethodSelector(pick([
        'accountFields',
        'accounts',
        'validationConfig',
        'defaultSuggestion',
        'suggestions',
        'amountRequired'
    ]));
    const [account] = usePortletSelector(prop('accounts'));
    const formConfig = hooks.useFormConfig({ accountFields, accounts, validationConfig, defaultSuggestion });
    const initialValues = Object.assign(formConfig.initialValues, { promoCode: getPromoCodeFromStore() });
    const t = usePortletTranslations();

    const isCardConfirmation = prop('storeCardConfirmationRequired', account);
    const [accordionId] = useState(uniqueId('accordion'));
    const isFieldWithValue = ({ name }) => path(['accountData', name], account) != null;
    const [hiddenFields, visibleFields] = partition(isFieldWithValue, accountFields);

    return <Form
        onSubmit={deposit}
        initialValues={initialValues}
        validationConfig={formConfig.validationConfig}
        fieldsToReplaceComma={['amount']}
        render={({ submitting, submitError, handleSubmit, values, form: { change, blur } }) => (
            <form
                className={style.form}
                onSubmit={event => event.preventDefault()}
            >
                {submitError && (
                    <ErrorMessage
                        error={submitError}
                        animate={true}
                    />
                )}

                {amountRequired && <Input
                    type="text"
                    name="amount"
                    inputMode="decimal"
                    label={t('amount.placeholder')}
                    placeholder={t('amount.placeholder')}
                    aria-label={t(['amount.placeholder', 'playtech.mobile.system.input.aria-label'])}
                />}

                <Suggestions onClick={helpers.getSuggestionsOnClick(change, blur)} suggestions={suggestions} />

                {/* TODO: in advance
                {!isQuickDeposit && <Select
                    name="storedAccountId"
                    label={t(`${tKeyPrefix}.${code}.accountId.label`)}
                    options={accountsOptions}
                />}
                */}

                {visibleFields.map((field, index) => (
                    <Field
                        key={index}
                        {...{ field, values }}
                    />)
                )}

                <Input
                    type="text"
                    name="promoCode"
                    label={t('promoCode.placeholder')}
                    placeholder={t('promoCode.placeholder')}
                    aria-label={t(['promoCode.placeholder', 'playtech.mobile.system.input.aria-label'])}
                />

                {isCardConfirmation && (
                    <p className={style.plainText}>
                        {t('card-confirmation')}
                    </p>
                )}

                {hiddenFields.length > 0 && (
                    <div>
                        <input type="checkbox" className={style.accordionAction} id={accordionId} />

                        <div className={style.accordionBody}>
                            {hiddenFields.map((field, index) =>
                                <Field key={index} {...{ field, values }} disabled={true} />
                            )}
                        </div>

                        <div className={style.accordionLabelHolder}>
                            <label htmlFor={accordionId} className={style.accordionLabel}>
                                {t('deposit.accordion.button')}
                            </label>
                        </div>
                    </div>
                )}

                <div className={style.buttons}>
                    <Button type="button" onClick={handleSubmit} disabled={submitting}>
                        {t('deposit.submit.button')}
                    </Button>
                </div>
            </form>
        )}
    />;
};

export const Field = ({ field, disabled = false, ...rest }) => {
    const { name, isPassword, label } = field;
    // TODO: remove after IMS improvements at (19.10.0) (MCBB-1300) https://ims-jira.playtech.corp/browse/IMS-164222
    const mobileNumericKeyboard = name === 'cvv2' ? { inputMode: 'numeric', pattern: '[0-9]*' } : {};

    return <Input
        type={isPassword ? 'password' : 'text'}
        name={name}
        label={label}
        placeholder={label}
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck="false"
        disabled={disabled}
        {...mobileNumericKeyboard}
        {...rest}
    />;
};
